import React, { useEffect, useRef, useState } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useSelector } from "react-redux";

const ProgressBar = ({
  textColor = "#ffffff",
  barColor = "#007bff",
  height = 8,
  defaultMessage = "Processing...",
}) => {
  const { tasks } = useSelector((state) => state.media); // Using state.media.tasks
  const [visibleTasks, setVisibleTasks] = useState(new Map()); // Track visible progress bars
  const progressRef = useRef(new Map()); // Store latest progress

  useEffect(() => {
    // Update visible tasks
    const newVisibleTasks = new Map(visibleTasks);

    tasks.forEach((task) => {
      progressRef.current.set(task.id, task.progress);
      if (!newVisibleTasks.has(task.id)) {
        newVisibleTasks.set(task.id, true); // Show progress bar for new task
      }
    });

    // Remove progress bars for tasks that no longer exist
    newVisibleTasks.forEach((_, taskId) => {
      if (!tasks.some((task) => task.id === taskId)) {
        newVisibleTasks.delete(taskId);
        progressRef.current.delete(taskId);
      }
    });

    setVisibleTasks(newVisibleTasks);

    // Cleanup on unmount
    return () => {
      progressRef.current.clear();
    };
  }, [tasks]);

  const renderProgressBar = (task) => {
    const currentProgress = progressRef.current.get(task.id) || task.progress;
    const isCompleted = currentProgress >= 100;

    return (
      <Box
        key={task.id}
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          borderRadius: "12px",
          minWidth: "350px",
          maxWidth: "500px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          margin: "8px auto",
          color: textColor,
          position: "fixed",
          top: `${50 + tasks.indexOf(task) * 120}px`, // Stack progress bars vertically
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1300, // Ensure above most UI elements
          padding: "12px 16px",
          display: visibleTasks.get(task.id) ? "flex" : "none",
          alignItems: "center",
          pointerEvents: "none", // Allow clicks to pass through
        }}
      >
        {isCompleted ? (
          <>
            <CheckCircle sx={{ color: "#4caf50", mr: 1, fontSize: 24 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {task.title || defaultMessage}: Completed
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, mb: 0.5, flex: 1 }}
            >
              {task.title || defaultMessage}: {Math.round(currentProgress)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={currentProgress}
              sx={{
                height,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: barColor,
                  transition: "width 0.3s ease-in-out",
                },
                borderRadius: 4,
                flex: 2,
              }}
            />
          </>
        )}
      </Box>
    );
  };

  return <>{tasks.map((task) => renderProgressBar(task))}</>;
};

export default ProgressBar;
